import useGetUser from '@/features/User/hooks/useGetUser';
import { CircleUserRound, Home, List } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import GifteeLogo from '@/assets/Giftee.png';
import useReactNative from '@/utils/useReactNative';
import useGetSavedLists from '@/features/List/hooks/useGetSavedLists';
import { setNotifications } from '@/utils/notifications';

const AUTHORIZED_PATHS = ['/home', '/lists', '/profile'];

type Props = {
	children: React.ReactNode;
};

const Navbar = ({ children }: Props) => {
	const { t, i18n } = useTranslation();
	const { user } = useGetUser();
	const location = useLocation();

	const homeActive = location.pathname === '/home';
	const listsActive = location.pathname === '/lists';
	const profileActive = location.pathname === '/profile';

	const { isReactNative, postMessage } = useReactNative();
	const { listsSaved } = useGetSavedLists(!!user);

	useEffect(() => {
		if (!isReactNative) return;
		if (!user) return;
		if (listsSaved.length === 0) return;

		const notifications = setNotifications(
			listsSaved,
			user,
			i18n.language.startsWith('fr') ? 'fr' : 'en'
		);

		notifications.sort((a, b) => a.date.getTime() - b.date.getTime());
		// iOS only allows 64 notifications
		notifications.splice(64);

		postMessage({ message: 'Notifications', notifications });
	}, [listsSaved]);

	useEffect(() => {
		if (homeActive || listsActive || profileActive) {
			window.scrollTo(0, 0);
		}
	}, [homeActive, listsActive, profileActive]);

	return (
		<>
			<div
				className={cn(
					'flex h-full w-full justify-center p-8',
					AUTHORIZED_PATHS.includes(location.pathname) && 'pb-20'
				)}
			>
				<div className="flex w-full sm:pl-16">{children}</div>
			</div>
			{user?.birthday && AUTHORIZED_PATHS.includes(location.pathname) && (
				<nav className="shadow-t dark:shadow-t-gray-800 fixed -bottom-[1px] left-0 right-0 flex h-16 w-full items-center justify-around bg-secondary sm:hidden">
					<Link
						to="/home"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={homeActive}
					>
						<Home className="h-6 w-6" />
						<span className="text-xs">{t('home.nav')}</span>
					</Link>
					<Link
						to="/lists"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={listsActive}
					>
						<List className="h-6 w-6" />
						<span className="text-xs">{t('savedLists.title')}</span>
					</Link>
					<Link
						to="/profile"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={profileActive}
					>
						<CircleUserRound className="h-6 w-6" />
						<span className="text-xs">{t('profile.title')}</span>
					</Link>
				</nav>
			)}
			{/* desktop */}
			{location.pathname !== '/' && (
				<div className="shadow-t dark:shadow-t-gray-800 bg-base fixed bottom-0 left-0 top-0 hidden h-full w-16 flex-col items-center gap-8 border-r py-4 sm:flex">
					<img
						src={GifteeLogo}
						alt="Giftee"
						className="mx-auto mt-3 w-[80%]"
					/>
					<Link
						to="/home"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={homeActive}
					>
						<Home className="h-5 w-5" />
						<span className="text-xs">{t('home.nav')}</span>
					</Link>
					<Link
						to="/lists"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={listsActive}
					>
						<List className="h-5 w-5" />
						<span className="text-xs">{t('savedLists.title')}</span>
					</Link>
					<Link
						to="/profile"
						className="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors hover:text-gray-900 data-[active=true]:text-primary dark:text-gray-400 dark:hover:text-gray-50 dark:data-[active=true]:text-primary"
						data-active={profileActive}
					>
						<CircleUserRound className="h-5 w-5" />
						<span className="text-xs">{t('profile.title')}</span>
					</Link>
				</div>
			)}
		</>
	);
};

export default Navbar;
